import { FC, ReactElement, useState, useEffect } from "react";
import {
  Box,
  Pagination,
  Stack,
  Typography,
  Select,
  MenuItem,
  PaginationItem,
} from "@mui/material";
import { ArrowBackIosNew, ArrowForwardIos } from "@mui/icons-material";
import { tokens } from "@/tokens";

interface Props {
  limit: number;
  page: number;
  total: number;
  handlePageChange?: (newPage: number) => void;
  handleLimitChange?: (newLimit: number) => void;
}

const TablePagination: FC<Props> = ({
  limit,
  page,
  total,
  handlePageChange,
  handleLimitChange,
}): ReactElement => {
  const [selectedRows, setselectedRows] = useState(limit);

  // Sync selectedRows with limit prop when it changes
  useEffect(() => {
    setselectedRows(limit);
  }, [limit]);

  const totalPages = Math.ceil(total / limit);
  const startItemIndex = (page - 1) * limit + 1;
  const endItemIndex = Math.min(page * limit, total);

  return (
    <Stack
      direction={{ xs: "column", sm: "row", md: "column", lg: "row" }}
      alignItems={{ xs: "start", sm: "center", md: "start", lg: "center" }}
      justifyContent="space-between"
      sx={{
        padding: "10px 16px",
        backgroundColor: tokens.bg.surface,
      }}
      spacing={2}
    >
      <Typography sx={{ fontWeight: tokens.fontWeight.normal, fontSize: tokens.fontSize.base, color: tokens.text.muted }}>
        Showing {startItemIndex} to {endItemIndex} of {total}
      </Typography>

      <Box sx={{ flex: 1, display: "flex", justifyContent: "center" }}>
        <Pagination
          count={totalPages}
          page={page}
          onChange={(_event, newPage: number) => {
            handlePageChange?.(newPage);
          }}
          renderItem={(item) => (
            <PaginationItem
              components={{
                previous: ArrowBackIosNew,
                next: ArrowForwardIos,
              }}
              {...item}
              sx={{
                width: 28,
                height: 28,
                minWidth: 28,
                fontSize: tokens.fontSize.base,
                "&.Mui-selected , &.Mui-selected:hover": {
                  backgroundColor: tokens.text.primary,
                  color: `${tokens.bg.surface} !important`,
                  fontWeight: tokens.fontWeight.bold,
                  borderRadius: tokens.radius.sm,
                  border: "unset",
                },
                "&.MuiPaginationItem-ellipsis": {
                  color: tokens.text.muted,
                },
                "&.MuiPaginationItem-text": {
                  color: tokens.text.secondary,
                },
                "&.MuiPaginationItem-previousNext": {
                  border: `1px solid ${tokens.border.default}`,
                  margin: "0px 12px",
                },
                "& .MuiPaginationItem-icon": {
                  color: tokens.text.secondary,
                  width: "14px",
                },
                "&:hover": {
                  backgroundColor: tokens.bg.muted,
                },

                borderRadius: tokens.radius.sm,
                margin: "0 2px",
              }}
            />
          )}
        />
      </Box>

      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Typography
          sx={{ fontWeight: tokens.fontWeight.normal, fontSize: tokens.fontSize.base, marginRight: "8px", color: tokens.text.muted }}
        >
          Show
        </Typography>
        <Select
          value={selectedRows}
          onChange={(event: any) => {
            setselectedRows(event.target.value);
            if (handleLimitChange) {
              handleLimitChange(Number(event.target.value));
            }
          }}
          renderValue={() => {
            return <Box px={1}>{selectedRows} Rows</Box>;
          }}
          sx={{
            fontSize: tokens.fontSize.base,
            height: "32px",
            color: tokens.text.secondary,
            "& .MuiSelect-select": {
              padding: "4px 8px",
              paddingLeft: "12px",
              paddingRight: "12px",
            },
          }}
        >
          {[10, 20, 25, 50].map((rows) => (
            <MenuItem key={rows} value={rows}>
              {rows} Rows
            </MenuItem>
          ))}
        </Select>
      </Box>
    </Stack>
  );
};

export default TablePagination;
